import 'whatwg-fetch';
import { create } from 'maska';
import parseISO from 'date-fns/parseISO';
import isValid from 'date-fns/isValid';

import { TREATMENT_TO_LOCATIONS, LASIK_LINKS } from './const';

/**
 * Set a buttons text, or enable/disable it.
 */
export const useSubmitButton = (button) => {
  if (!button) return;

  const el = button.querySelector('.Button-content') || button;

  const setText = (text) => (el.textContent = text);
  const disable = () => el.setAttribute('disabled', true);
  const enable = () => el.removeAttribute('disabled');

  return {
    setText,
    disable,
    enable,
  };
};

/**
 * Handle form error DOM updates
 */
export const useFormError = (el, errorTargetSelector = '[data-target="error"]') => {
  if (!el) return;

  const errorEl = el.querySelector(errorTargetSelector);
  if (!errorEl) return;

  const setError = (text) => {
    el.classList.add('block');
    el.classList.remove('hidden');
    errorEl.innerHTML = `<p class="mb-2">${text}</p>`;
  };
  const clearError = () => {
    el.classList.add('hidden');
    el.classList.remove('block');
    errorEl.innerHTML = '';
  };

  return {
    setError,
    clearError,
  };
};

/**
 * Send an AJAX submission to WordPress
 */
export const useFormSend = ({ form, onBefore, onSuccess, onFail, onFinally }) => {
  const send = () => {
    onBefore && onBefore();

    return fetch(window.app.ajax.url, {
      method: 'post',
      body: new FormData(form),
    })
      .then((response) => response.json())
      .then((response) => {
        if (!response.success) {
          return onFail && onFail(response);
        }
        onSuccess && onSuccess(response);
      })
      .catch((error) => {
        onFail && onFail(error);
      })
      .finally((response) => {
        onFinally && onFinally(response);
      });
  };

  return {
    send,
  };
};

/**
 * Add input masks for phone numbers and dates
 */
export const useInputMasks = ({ dateSelector, phoneSelector }) => {
  if (dateSelector) {
    create(dateSelector, {
      mask: '####-##-##',
    });
  }
  if (phoneSelector) {
    create(phoneSelector, {
      mask: '###-###-####',
    });
  }
};

/**
 * Dynamic location select based on selected treatment
 *
 * Also handles updating the "Book Online" appointment button for LASIK
 */
export const useDynamicSelects = (form) => {
  const treatmentInput = form.querySelector('[name="treatment"]');
  const locationInput = form.querySelector('[name="location"]');
  const zipInput = form.querySelector('[name="zip"]');

  if (treatmentInput && locationInput && zipInput) {
    // Handle treatment changes
    treatmentInput.addEventListener('change', (event) => {
      // console.log('Treatment changed:', event.target.value);
      const locations = TREATMENT_TO_LOCATIONS[event.target.value] || [];
      updateLocationValues(locationInput, locations, event.target.value === '');
    });

    // Handle zip code changes
    zipInput.addEventListener('change', (event) => {
      // console.log('ZIP changed:', event.target.value);
      if (treatmentInput.value) {
        const locations = TREATMENT_TO_LOCATIONS[treatmentInput.value] || [];
        updateLocationValues(locationInput, locations, false);
      }
    });
  } else {
    // console.error('Missing required form elements:', {
    //   treatment: !!treatmentInput,
    //   location: !!locationInput,
    //   zip: !!zipInput
    // });
  }
};

// Update location options from locations list for a treatment
const updateLocationValues = (locationInput, locations = [], isReset = false) => {
  const form = locationInput.closest('form');
  const zipInput = form.querySelector('[name="zip"]');
  const treatmentInput = form.querySelector('[name="treatment"]');

  // console.log('Starting updateLocationValues');
  // console.log('Available locations for filtering:', locations);

  // Skip filtering for ECRF form - all locations are visible regardless of treatment selected for the ECRF form only
  if (form.getAttribute('data-name') === 'eCRF') {
    const options = locationInput.querySelectorAll('option');
    options.forEach(option => {
      option.style.display = 'initial';
    });
  } else {
    // Existing filtering logic for other forms
    if (treatmentInput && treatmentInput.value === 'Other') {
      const options = locationInput.querySelectorAll('option');
      options.forEach(option => {
        option.style.display = 'initial';
        if (option.value) {
          locations.push(option.value);
        }
      });
    } else if (treatmentInput && treatmentInput.value === 'Corneal Cross-Linking') {
      // Use exact match from const.js for Cross-Linking
      const options = locationInput.querySelectorAll('option');
      options.forEach((option) => {
        if (isReset || TREATMENT_TO_LOCATIONS['Corneal Cross-Linking'].includes(option.value) || option.value === '') {
          option.style.display = 'initial';
        } else {
          option.style.display = 'none';
        }
      });
    } else {
      // Filter options for other treatments
      const options = locationInput.querySelectorAll('option');
      options.forEach((option) => {
        if (isReset || locations.includes(option.value) || option.value === '') {
          option.style.display = 'initial';
        } else {
          option.style.display = 'none';
        }
      });
    }
  }

  // Location field is always required and visible
  locationInput.required = true;

  // Only search for nearest location if we have both treatment and valid zip
  const hasValidZip = zipInput && zipInput.value.length === 5;
  const hasLocations = locations && locations.length > 0;

  if (hasValidZip && hasLocations) {
    handleZipCodeSearch(zipInput, locationInput, locations);
  }

  return locations;
};

// Update "Book Online" appointment button with link for LASIK locations
const updateBookLasikButton = (el, treatment, location) => {
  // console.log('updateBookLasikButton called with:', { el, treatment, location });

  // If no element is found, do nothing
  if (!el) {
    // console.warn('No external booking element found');
    return;
  }

  // Get the location label from the selected option
  const locationSelect = document.querySelector('[name="location"]');
  const selectedOption = locationSelect?.querySelector(`option[value="${location}"]`);
  const locationLabel = selectedOption?.textContent?.trim();

  // console.log('Location details:', {
  //   value: location,
  //   label: locationLabel,
  //   hasMatchingLink: LASIK_LINKS[locationLabel]
  // });

  const link = LASIK_LINKS[locationLabel];
  const linkEl = el.querySelector('[data-target="link"]');

  // Always hide first, then show only if all conditions are met
  el.classList.add('hidden');
  el.classList.remove('flex');

  // If all conditions are met, show the button and update the link
  if (treatment === 'LASIK' && locationLabel && link && linkEl) {
    // console.log('Showing button with link:', link);
    el.classList.add('flex');
    el.classList.remove('hidden');
    linkEl.setAttribute('href', link);
  } else {
    // console.log('Hiding button because:', {
    //   isLasik: treatment === 'LASIK',
    //   hasLocation: !!locationLabel,
    //   hasLink: !!link,
    //   hasLinkElement: !!linkEl
    // });
  }
};

/**
 * Dynamic lead type to lead attribution
 */
export const useDynamicLeadAttribution = (typeEl, attributionEl, valueMap) => {
  if (!(typeEl && attributionEl && valueMap)) return;

  const attributionOptions = attributionEl.querySelectorAll('option');

  typeEl.addEventListener('change', (event) => {
    const typeValue = event.target.value;
    const allowedAttributions = valueMap[typeValue];

    attributionEl.value = '';

    attributionOptions.forEach((option) => {
      if (
        typeValue === '' ||
        allowedAttributions.includes(option.value) ||
        option.value === ''
      ) {
        option.style.display = 'initial';
        return;
      }
      option.style.display = 'none';
    });
  });
};

// Fire a tracking pixel
export const usePixel = (url) => {
  const img = new Image();
  img.src = url;
};

// Validate a date on input
// Expects YYYY-MM-DD
export const useValidDate = (input) => {
  if (!input) return;

  input.addEventListener('input', (event) => {
    const { value } = event.target;

    if (value === '') {
      input.setCustomValidity('');
      return;
    }

    const parsedDate = parseISO(value);

    if (value.length === 10 && isValid(parsedDate)) {
      input.setCustomValidity('');
      return;
    }

    input.setCustomValidity('Please input date in the format: YYYY-MM-DD.');
  });
};

// Custom error message on input
export const useInputCustomError = (els) => {
  if (!(els && els.length > 0)) return;

  try {
    els.forEach((el) => {
      const errorMessage = el.getAttribute('data-error-message');

      el.addEventListener('invalid', () => {
        el.setCustomValidity('');
        if (el.validity.valueMissing) {
          el.setCustomValidity('Please fill out this field.');
        } else {
          el.setCustomValidity(errorMessage);
        }
      });

      el.addEventListener('change', () => {
        el.setCustomValidity('');
      });
    });
  } catch (error) { }
};

// Form logging errors
export const formatLoggingError = (errors) =>
  errors.reduce(
    (acc, err, index) =>
      (acc += `${index > 0 ? ', ' : ''}${err.errorCode}: ${err.message}`),
    ''
  );

// Other input
export const useOtherInput = ({
  input,
  target,
  targetInput,
  otherValue = 'Other',
  visibilityClass = 'hidden',
}) => {
  if (!(input && target && targetInput)) {
    return;
  }

  const handleChange = () => {
    if (input.value === otherValue) {
      target.classList.remove(visibilityClass);
      return;
    }

    // When hiding the other field, reset it's value too
    targetInput.value = '';
    target.classList.add(visibilityClass);
  };

  handleChange();

  input.addEventListener('change', handleChange);
};

const handleZipCodeSearch = (zipInput, locationInput, visibleLocations) => {
  // Only trigger the search if we have a valid ZIP
  if (!zipInput.value || zipInput.value.length !== 5) {
    return;
  }

  // Set loading state ONLY after ZIP is entered
  const defaultOption = locationInput.querySelector('option[value=""]');
  if (defaultOption) {
    defaultOption.textContent = 'Finding nearest location...';
  }
  locationInput.disabled = true;
  locationInput.value = '';

  const formData = new FormData();
  formData.append('action', 'get_nearest_location');
  formData.append('zip', zipInput.value);
  formData.append('locations', JSON.stringify(visibleLocations));

  fetch('/wp-admin/admin-ajax.php', {
    method: 'POST',
    body: formData
  })
    .then(response => response.json())
    .then(data => {
      locationInput.disabled = false;

      if (data.success && data.data?.location && data.data?.withinRange === true) {
        locationInput.value = data.data.location;

        // Add LASIK button check here
        const form = locationInput.closest('form');
        const treatmentInput = form.querySelector('[name="treatment"]');
        const externalBooking = form.querySelector('[data-plugin="external-lasik-book"]');

        if (externalBooking) {
          updateBookLasikButton(
            externalBooking,
            treatmentInput?.value || 'LASIK',
            data.data.location
          );
        }
      } else {
        locationInput.value = '';
        if (defaultOption) {
          defaultOption.textContent = 'No nearby locations found. Please choose a location.';
        }
      }
    })
    .catch(error => {
      // console.error('ZIP search error:', error);
      locationInput.disabled = false;
      locationInput.value = '';
      if (defaultOption) {
        defaultOption.textContent = 'No nearby locations. Please choose one.';
      }
    });
};

// Add form validation helper
const validateForm = (form) => {
  const locationInput = form.querySelector('[name="location"]');
  const locationField = locationInput?.closest('.location-field');

  // If location field is visible, make sure it has a value
  if (locationField && locationField.style.display !== 'none') {
    if (!locationInput.value) {
      locationInput.setCustomValidity('Please select a location');
      return false;
    }
  }

  locationInput?.setCustomValidity('');
  return true;
};

// Add event listeners when the form loads
document.addEventListener('DOMContentLoaded', () => {
  const form = document.getElementById('book-form');
  if (!form) return;

  const treatmentInput = form.querySelector('[name="treatment"]');
  const locationInput = form.querySelector('[name="location"]');
  const zipInput = form.querySelector('[name="zip"]');

  if (treatmentInput && locationInput && zipInput) {
    // Handle treatment changes
    treatmentInput.addEventListener('change', (event) => {
      // console.log('Treatment changed:', event.target.value);
      updateLocationValues(locationInput, TREATMENT_TO_LOCATIONS[event.target.value] || [], event.target.value === '');
    });

    // Handle zip code changes
    zipInput.addEventListener('change', (event) => {
      if (treatmentInput.value) {
        updateLocationValues(locationInput, TREATMENT_TO_LOCATIONS[treatmentInput.value] || [], false);
      }
    });

    // Add form validation
    form.addEventListener('submit', (event) => {
      if (!validateForm(form)) {
        event.preventDefault();
      }
    });
  }
});

// Add this where you make the AJAX call
function updateLocationField(zip, locations) {
  const locationField = document.querySelector('[name="location"]');
  if (locationField) {
    // Keep field visible but show loading state
    locationField.value = 'Finding nearest location...';
    locationField.disabled = true; // Disable while searching

    // Make your AJAX call
    fetch('/wp-admin/admin-ajax.php', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams({
        action: 'get_nearest_location',
        zip: zip,
        locations: JSON.stringify(locations)
      })
    })
      .then(response => response.json())
      .then(data => {
        if (data.success && data.data.location) {
          locationField.value = data.data.location;

          // Add LASIK button check here
          const form = locationField.closest('form');
          const treatmentInput = form.querySelector('[name="treatment"]');
          const externalBooking = form.querySelector('[data-plugin="external-lasik-book"]');

          if (externalBooking) {
            updateBookLasikButton(
              externalBooking,
              treatmentInput?.value || 'LASIK',
              data.data.location
            );
          }
        }
        locationField.disabled = false;
      })
      .catch(error => {
        // console.error('Error:', error);
        locationField.value = ''; // or some default value
        locationField.disabled = false; // Re-enable after error
      });
  }
}

export const useForm = (form) => {
  if (!form) {
    return;
  }

  // console.group('Form Initialization');

  // Add external booking functionality
  const locationSelect = form.querySelector('[name="location"]');
  const treatmentSelect = form.querySelector('[name="treatment"]');
  const externalBooking = form.querySelector('[data-plugin="external-lasik-book"]');

  // console.log('Form elements:', {
  //   form,
  //   locationSelect: locationSelect?.value,
  //   treatmentSelect: treatmentSelect?.value,
  //   externalBooking: !!externalBooking
  // });

  if (locationSelect && externalBooking) {
    const handleExternalBooking = () => {
      // console.group('External Booking Update');
      const location = locationSelect.value;
      const treatment = treatmentSelect?.value || 'LASIK';
      // console.log('Values:', { location, treatment });
      updateBookLasikButton(externalBooking, treatment, location);
      // console.groupEnd();
    };

    // console.log('Adding event listeners');
    locationSelect.addEventListener('change', handleExternalBooking);
    if (treatmentSelect) {
      treatmentSelect.addEventListener('change', handleExternalBooking);
    }

    // Initial check
    handleExternalBooking();
  } else {
    // console.warn('Missing required elements:', {
    //   hasLocationSelect: !!locationSelect,
    //   hasExternalBooking: !!externalBooking
    // });
  }
};

// Add event listeners when the form loads
document.addEventListener('DOMContentLoaded', () => {
  const form = document.getElementById('book-form');
  if (!form) return;

  const treatmentInput = form.querySelector('[name="treatment"]');
  const locationInput = form.querySelector('[name="location"]');
  const externalBooking = form.querySelector('[data-plugin="external-lasik-book"]');

  if (locationInput && externalBooking) {
    // Handle manual location changes
    locationInput.addEventListener('change', (event) => {
      // console.log('Location manually changed:', event.target.value);
      updateBookLasikButton(
        externalBooking,
        treatmentInput?.value || 'LASIK',
        event.target.value
      );
    });

    // Handle treatment changes
    if (treatmentInput) {
      treatmentInput.addEventListener('change', (event) => {
        // console.log('Treatment changed:', event.target.value);
        updateBookLasikButton(
          externalBooking,
          event.target.value,
          locationInput.value
        );
      });
    }
  }
});
